import React from 'react';
import HubErrorPage from './HubErrorPage';
import ErrorBoundaryInline from './ErrorBoundaryInline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      level: this.props.level || 'component',
      retryCount: this.state.retryCount,
    };

    if (process.env.NODE_ENV === 'development') {
      console.error('Error boundary details:', errorData);
    }
  };

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  buildSupportCode = (error) => {
    if (!error?.message) {
      return null;
    }
    return String(error.message).slice(0, 48);
  };

  buildDevDetails = (error, errorInfo) => {
    if (process.env.NODE_ENV !== 'development' || !error) {
      return null;
    }

    const body = [
      `Error: ${error.message}`,
      '',
      'Stack Trace:',
      error.stack || '',
      errorInfo?.componentStack ? `\nComponent Stack:${errorInfo.componentStack}` : '',
    ].join('\n');

    return {
      summary: 'Error details (development only)',
      body,
    };
  };

  renderFallbackUI = () => {
    const {
      level = 'component',
      showRetry = true,
      showHome = true,
      compact = false,
    } = this.props;
    const { error, errorInfo } = this.state;

    if (compact) {
      return (
        <ErrorBoundaryInline onRetry={this.handleRetry} showRetry={showRetry} />
      );
    }

    return (
      <HubErrorPage
        type="unexpected"
        variant={level === 'app' ? 'standalone' : 'shell'}
        onRetry={this.handleRetry}
        showRetry={showRetry}
        showBack={level !== 'app'}
        showDashboard={showHome && level !== 'component'}
        supportCode={this.buildSupportCode(error)}
        devDetails={this.buildDevDetails(error, errorInfo)}
      />
    );
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo,
          this.handleRetry,
        );
      }

      return this.renderFallbackUI();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
